// Phase E-2 PowerSync-backed capture client adapter.
//
// Routes every CaptureMutation kind through PowerSync's local SQLite +
// CRUD upload queue (see the `dispatch` switch below). The REST adapter's
// `rest.dispatch(mutation)` arm stays as a fallback (currently no kind uses
// it) + owns the shared auth/queue/retry state machine.
//
// Coalesce: 500 ms per-itemId debounce on updateItem, collapsing rapid
// +/- taps to ONE local UPDATE + ONE CRUD entry (else five taps → five PUTs).
//
// `updated_at` is provisional client-side (SQLite `now()`); the Postgres
// trigger HP1/migration 0005 overwrites it with the server time on upload (LWW).
//
// DTO contract: dispatch() + item-changed event MUST resolve a
// camelCase CaptureItemRow joined with asset-type metadata. Screens
// read `.assetTypeId`/`.syncVersion` etc. directly — emitting raw
// snake_case would silently break their reconcilers. Assembled via a
// 4-table JOIN (capture_items + asset_types + asset_sub_systems +
// asset_systems), kept populated by the `captures`/`reference_global` streams.

import {
  createCaptureClient,
  type CaptureClientDeps,
  type CaptureClientWithInternals,
} from './capture-client'
import { defaultIsOnline } from './capture-client-defaults'
import { getDefaultApiFetch } from './capture-client-transport'
import { eventForResult } from './capture-client-events'
import {
  createCaptureEventBus,
  type CaptureEventBus,
  type CaptureEventHandler,
} from './capture-events'
import type {
  CaptureMutation,
  CaptureMutationResult,
} from './capture-client.types'
import { CAPTURE_ITEM_FLUSH_SQL } from './capture-item-flush-sql'

import {
  committedQuantity,
  createCaptureLifecycle,
  requeueFailedFlush,
  trackCoalesceFlush,
  type CoalesceEntry,
} from './capture-client-powersync-lifecycle'
import { dispatchCancelSession } from './capture-client-powersync-cancel-session'
import { dispatchCreateItem } from './capture-client-powersync-create-item'
import { dispatchCreateFieldAddedVariant } from './capture-client-powersync-create-field-added'
import {
  restCreateSessionOnce,
  routeCreateSessionDispatch,
} from './capture-client-powersync-create-session-route'
import { dispatchCreateSpace } from './capture-client-powersync-create-space'
import type { PowerSyncDb } from './capture-client-powersync-db'
import { dispatchDeleteSpace } from './capture-client-powersync-delete-space'
import { dispatchSessionStateChange } from './capture-client-powersync-session-state'
import { withEnqueueSignal } from './capture-client-powersync-enqueue-signal'
import { dispatchUpdateSpaceAlias } from './capture-client-powersync-update-space-alias'

const DEFAULT_DEBOUNCE_MS = 500

// PowerSyncDb / PowerSyncWriteTx are defined in a sibling module (to keep this
// file under the line cap) and re-exported here so existing
// `from './capture-client-powersync'` imports keep resolving.
export type {
  PowerSyncDb,
  PowerSyncWriteTx,
} from './capture-client-powersync-db'

export interface PowerSyncCaptureClientDeps extends CaptureClientDeps {
  /**
   * The PowerSync database instance the adapter writes against. In
   * production this is the singleton from `hooks/powersync/client.ts`;
   * tests inject a fake that records `execute` / `getAll` calls.
   */
  db: PowerSyncDb
}

export function createPowerSyncCaptureClient(
  deps: PowerSyncCaptureClientDeps,
): CaptureClientWithInternals {
  // Wrap the db so every queue-generating local write emits the held-work
  // enqueue pulse — the one signal the held-count indicator cannot otherwise see
  // when a +/- tap lands in a `navigator.onLine === true` dead zone (#754). One
  // chokepoint covers every current and future dispatch path.
  const db = withEnqueueSignal(deps.db)
  const debounceMs = deps.debounceMs ?? DEFAULT_DEBOUNCE_MS
  const bus: CaptureEventBus = deps.eventBus ?? createCaptureEventBus()

  // Delegate every kind that ISN'T updateItem to the REST adapter; share the
  // event bus so subscribe() sees one stream. #816: pass no-op tab-lifecycle
  // seams so `createCaptureLifecycle` (installLifecycleFlush) is the SOLE owner
  // of the visibilitychange/pagehide/beforeunload flush. Without the overrides
  // the delegate registers its OWN beforeunload + visibilitychange on the same
  // window — inert today (no kind routes to `rest.dispatch`, so the delegate's
  // queue/coalesce stay empty and its handlers no-op) but a drift surface: the
  // moment a kind falls through to `rest.dispatch`, that ungated handler would
  // become a second, divergent flush impl alongside the one pinned by
  // `lint:powersync-capture-lifecycle-flush`. The ORIGINAL `deps` seams still
  // reach `createCaptureLifecycle` below, so the LIVE PowerSync flush is
  // untouched. The delegate's online/offline seams are intentionally left as-is
  // (single-owner network listeners, not duplicated by the lifecycle wiring).
  const noopLifecycleListener = (): (() => void) => () => {}
  const rest = createCaptureClient({
    ...deps,
    eventBus: bus,
    addBeforeUnloadListener: noopLifecycleListener,
    addVisibilityChangeListener: noopLifecycleListener,
  })
  // The createSession router's divert is ONE bounded fetch, never the REST
  // queue (audit-1-#1 / Audit-2 P1 #1); same connectivity signal as the queue.
  const isOnline = deps.isOnline ?? defaultIsOnline
  const fetchImpl = deps.apiFetch ?? getDefaultApiFetch()

  const coalesce = new Map<string, CoalesceEntry>()
  const inFlightCoalesce = new Set<Promise<void>>()

  function dispatch(
    mutation: CaptureMutation,
  ): Promise<CaptureMutationResult> {
    if (mutation.kind === 'updateItem') {
      return dispatchUpdateItem(mutation)
    }
    if (
      mutation.kind === 'completeSession' ||
      mutation.kind === 'reopenSession'
    ) {
      return dispatchSessionStateChange(mutation, db, bus)
    }
    if (mutation.kind === 'createItem') {
      return dispatchCreateItem(mutation, db, bus)
    }
    if (mutation.kind === 'createFieldAddedVariant') {
      return dispatchCreateFieldAddedVariant(mutation, db, bus)
    }
    if (mutation.kind === 'createSpace') {
      return dispatchCreateSpace(mutation, db, bus)
    }
    if (mutation.kind === 'updateSpaceAlias') {
      // FFR2 — local-first room rename. Optimistic `UPDATE spaces SET
      // alias` writes the local mirror immediately and queues a
      // spaces-PATCH the connector PUTs to the alias endpoint on
      // reconnect (offline-capable). See the dispatch file's header.
      return dispatchUpdateSpaceAlias(mutation, db, bus)
    }
    if (mutation.kind === 'createSession') {
      // Resume-vs-create gate (…-create-session-route.ts): one BOUNDED REST
      // attempt for an off-mirror completed building, else local-first.
      const viaRest = (m: typeof mutation): Promise<CaptureMutationResult> =>
        restCreateSessionOnce(fetchImpl, m)
      return routeCreateSessionDispatch(mutation, db, bus, viaRest, isOnline)
    }
    if (mutation.kind === 'cancelSession') {
      return dispatchCancelSession(mutation, db, bus)
    }
    if (mutation.kind === 'deleteSpace') {
      // #596 / ADR-015 — local-first room delete. Writes a tombstone +
      // cascade-DELETEs capture_items + DELETEs the spaces row locally; the
      // connector reads the tombstone on the queued spaces-DELETE and
      // propagates on reconnect. See connector-dispatch-space-delete.ts.
      return dispatchDeleteSpace(mutation, db, bus)
    }
    // Fall-through to the REST adapter for any kind not wired through
    // PowerSync (currently none — every kind has a local-first arm above);
    // kept for safety + the shared auth/queue/retry state machine.
    return rest.dispatch(mutation)
  }

  // dispatchSessionStateChange (completeSession / reopenSession) extracted to
  // capture-client-powersync-session-state.ts to keep this file under the
  // 500-line gate (audit-1 #9). See that file's header for the same-state-wedge
  // gate contract.

  function dispatchUpdateItem(
    mutation: Extract<CaptureMutation, { kind: 'updateItem' }>,
  ): Promise<CaptureMutationResult> {
    return new Promise<CaptureMutationResult>((resolve, reject) => {
      const resolver = { resolve, reject }
      const existing = coalesce.get(mutation.itemId)
      if (existing !== undefined) {
        // Latest tap wins — same semantic as the REST adapter (PRD
        // §7.3.4 debounces on final state, not the sum of deltas).
        clearTimeout(existing.debounceTimer)
        existing.current = mutation
        existing.resolvers.push(resolver)
        existing.debounceTimer = setTimeout(() => {
          // .catch: see the fresh-entry timer below for why this is swallowed.
          flushCoalesce(mutation.itemId).catch(() => {})
        }, debounceMs)
        return
      }
      const entry: CoalesceEntry = {
        itemId: mutation.itemId,
        current: mutation,
        resolvers: [resolver],
        debounceTimer: setTimeout(() => {
          // .catch: flushCoalesce re-throws on a failed persist so flush() fails
          // closed; the timer has no awaiter (dispatch promise already rejected).
          flushCoalesce(mutation.itemId).catch(() => {})
        }, debounceMs),
      }
      coalesce.set(mutation.itemId, entry)
    })
  }

  async function flushCoalesce(itemId: string): Promise<void> {
    const entry = coalesce.get(itemId)
    if (entry === undefined) return
    clearTimeout(entry.debounceTimer)
    coalesce.delete(itemId)

    const flushPromise = (async (): Promise<void> => {
      try {
        // Local-first: write to SQLite immediately. PowerSync also adds a
        // CRUD entry to its internal upload queue — the connector's
        // uploadData() picks that up on the next sync cycle. Reactive
        // useQuery() consumers see the local UPDATE on the next tick.
        //
        // sync_version is advanced by an ATOMIC in-SQL read-modify-write
        // (`sync_version = sync_version + 1`) against the durable local row, NOT
        // from the React-optimistic entry.current.syncVersion (which lags the
        // item-changed echo). This keeps every flush strictly monotonic
        // regardless of echo timing, so the column ALWAYS changes: otherwise two
        // flushes before the echo landed re-wrote the same value → onlyWhenChanged
        // stripped sync_version → a syncVersion-less PUT → Encore 400 → rethrow →
        // the whole device upload queue WEDGED. previousValues still carries the
        // pre-bump base the backend's optimistic gate validates (happy path
        // byte-identical N→N+1; offline bursts advance N+1,N+2,…). NOTE: the
        // connector reapply path (connector-lww-reapply.ts) writes an ABSOLUTE
        // `sync_version = ?` and MUST stay absolute.
        await db.execute(CAPTURE_ITEM_FLUSH_SQL, [
          committedQuantity(entry.current.quantity),
          entry.current.isZeroConfirmed ? 1 : 0,
          entry.current.notes,
          entry.current.itemId,
        ])

        // JOIN against the synced reference tables to assemble the same
        // CaptureItemRow shape the REST adapter returns. Snake_case
        // columns are aliased to camelCase via SQLite `AS`, booleans
        // are CAST from INTEGER 0/1 to JS truthy values below.
        const rows = await db.getAll<Record<string, unknown>>(
          `SELECT
           ci.id              AS id,
           ci.asset_type_id   AS assetTypeId,
           at.name            AS assetTypeName,
           at.unit_of_measure AS unitOfMeasure,
           at.grouping_convention AS groupingConvention,
           ass.id             AS systemId,
           ass.name           AS systemName,
           asub.id            AS subSystemId,
           asub.name          AS subSystemName,
           ci.quantity        AS quantity,
           ci.is_zero_confirmed AS isZeroConfirmedRaw,
           ci.notes           AS notes,
           ci.captured_at     AS capturedAt,
           ci.updated_at      AS updatedAt,
           ci.sync_version    AS syncVersion,
           at.is_field_added  AS isFieldAddedRaw,
           at.requires_review AS requiresReviewRaw
         FROM capture_items ci
         LEFT JOIN asset_types at       ON at.id   = ci.asset_type_id
         LEFT JOIN asset_sub_systems asub ON asub.id = at.sub_system_id
         LEFT JOIN asset_systems ass    ON ass.id  = asub.system_id
           WHERE ci.id = ?
           LIMIT 1`,
          [entry.current.itemId],
        )

        // Defensive fallback: if the JOIN didn't return (shouldn't
        // happen — the UPDATE just ran), synthesise a minimal row from
        // the mutation so downstream reconcilers don't crash on
        // undefined. The reference fields are filled with empty strings
        // rather than null/undefined so `'syncVersion' in result.item`
        // checks remain true (site-capture-screen.tsx:330-344 gates
        // version updates on that key's presence).
        const raw = rows[0]
        const item = raw
          ? {
              id: String(raw['id'] ?? entry.current.itemId),
              assetTypeId: String(raw['assetTypeId'] ?? ''),
              assetTypeName: String(raw['assetTypeName'] ?? ''),
              unitOfMeasure: String(raw['unitOfMeasure'] ?? 'Count'),
              groupingConvention: String(raw['groupingConvention'] ?? ''),
              systemId: String(raw['systemId'] ?? ''),
              systemName: String(raw['systemName'] ?? ''),
              subSystemId: String(raw['subSystemId'] ?? ''),
              subSystemName: String(raw['subSystemName'] ?? ''),
              quantity: String(raw['quantity'] ?? entry.current.quantity),
              isZeroConfirmed: raw['isZeroConfirmedRaw'] === 1,
              notes:
                raw['notes'] === null || raw['notes'] === undefined
                  ? null
                  : String(raw['notes']),
              capturedAt: String(raw['capturedAt'] ?? ''),
              updatedAt: String(raw['updatedAt'] ?? ''),
              syncVersion: Number(raw['syncVersion'] ?? entry.current.syncVersion),
              isFieldAdded: raw['isFieldAddedRaw'] === 1,
              requiresReview: raw['requiresReviewRaw'] === 1,
              // Always false for updateItem — by definition the row is
              // persisted, not template-only.
              isTemplateOnly: false,
            }
          : {
              id: entry.current.itemId,
              assetTypeId: '',
              assetTypeName: '',
              unitOfMeasure: 'Count',
              groupingConvention: '',
              systemId: '',
              systemName: '',
              subSystemId: '',
              subSystemName: '',
              quantity: entry.current.quantity,
              isZeroConfirmed: entry.current.isZeroConfirmed,
              notes: entry.current.notes,
              capturedAt: '',
              updatedAt: '',
              syncVersion: entry.current.syncVersion,
              isFieldAdded: false,
              requiresReview: false,
              isTemplateOnly: false,
            }
        const result: CaptureMutationResult = {
          kind: 'updateItem',
          item,
        }
        bus.emit(eventForResult(entry.current, result))
        for (const resolver of entry.resolvers) resolver.resolve(result)
      } catch (err) {
        for (const resolver of entry.resolvers) resolver.reject(err)
        // F6 (#811): re-queue the failed count so it stays HELD (pendingCount + logout fail-closed), not silently dropped; re-throw so flush() fails closed.
        requeueFailedFlush(coalesce, entry, flushCoalesce, debounceMs)
        throw err
      }
    })()
    return trackCoalesceFlush(inFlightCoalesce, flushPromise)
  }

  function pendingCount(): number {
    return coalesce.size + inFlightCoalesce.size + rest.pendingCount()
  }

  function subscribe(handler: CaptureEventHandler): () => void {
    return bus.subscribe(handler)
  }

  // flush()/destroy() + the #807 tab-lifecycle coalesce flush live in
  // capture-client-powersync-lifecycle.ts (line-cap extraction; see its header
  // for the #726 logout drain + the visibilitychange/pagehide background flush).
  const { flush, destroy } = createCaptureLifecycle({
    coalesce,
    flushCoalesce,
    inFlight: inFlightCoalesce,
    rest,
    pendingCount,
    deps,
  })

  return { dispatch, subscribe, pendingCount, flush, destroy }
}
